import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  listSupplierPayments,
  type SupplierPaymentListSort,
} from "@/shared/api/purchasing";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { DocListLegend } from "@/modules/core/components/DocListLegend";
import { POSTED_DOC_LIFECYCLE_LEGEND } from "@/modules/core/components/lifecycle-status";
import type {
  PartyFilter,
  PostedDocLifecycleFilter,
} from "@/modules/core/list-cell-actions";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { ActiveFilterChips } from "@/modules/shell/ActiveFilterChips";
import {
  paramOr,
  readPartyFilter,
  readQ,
  setOrDelete,
  writePartyFilter,
  LIST_SEARCH_DEBOUNCE_MS,
} from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { purchasingModuleTabs } from "@/modules/shell/desk-module-tabs";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { SavedListViewsControl } from "@/modules/shell/SavedListViewsControl";
import { takeListReturn } from "@/modules/shell/list-return-state";
import { useListRowFlash } from "@/modules/shell/use-list-row-flash";
import { supplierPaymentColumns } from "./supplier-payments-columns";

const PAGE_SIZE = 50;
const LIFECYCLES = ["all", "draft", "posted", "void"] as const;
const SORTABLE: SupplierPaymentListSort[] = [
  "number",
  "supplierName",
  "amount",
  "lifecycle",
];

function sortingToApi(sorting: SortingState): {
  sort: SupplierPaymentListSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (first && SORTABLE.includes(first.id as SupplierPaymentListSort)) {
    return {
      sort: first.id as SupplierPaymentListSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "number", sortDir: "asc" };
}

export function SupplierPaymentsPage() {
  const { companySlug } = useParams();
  const { searchParams, patchParams, replaceAllParams } = useListUrlParams();
  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));
  const lifecycleFilter = paramOr(
    searchParams,
    "lifecycle",
    LIFECYCLES,
    "all",
  ) as PostedDocLifecycleFilter;
  const setLifecycleFilter = (next: PostedDocLifecycleFilter) =>
    patchParams((p) => setOrDelete(p, "lifecycle", next));
  const partyFilter = readPartyFilter(searchParams);
  const setPartyFilter = (next: PartyFilter | null) => {
    patchParams((p) => writePartyFilter(p, next));
  };
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sort, sortDir } = sortingToApi(sorting);
  const serverLifecycle =
    lifecycleFilter === "all" ? undefined : lifecycleFilter;
  const listPath = companySlug
    ? `/c/${companySlug}/purchasing/payments`
    : "/purchasing/payments";
  const listReturnHandled = useRef(false);
  const { flashId, flashRow } = useListRowFlash();

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      patchParams((p) => setOrDelete(p, "q", q));
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, patchParams]);

  const paymentsQuery = useInfiniteQuery({
    queryKey: queryKeys.payments.suppliers(companySlug ?? "", {
      q: debouncedQ,
      lifecycle: serverLifecycle ?? "all",
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listSupplierPayments(companySlug!, {
        q: debouncedQ || undefined,
        lifecycle: serverLifecycle,
        limit: PAGE_SIZE,
        cursor: pageParam,
        sort,
        sortDir,
        signal,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });
  const errorMsg = queryErrorMessage(
    paymentsQuery.error,
    paymentsQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const columns = useMemo(
    () =>
      companySlug
        ? supplierPaymentColumns(companySlug, {
            listPath,
            partyFilter,
            setPartyFilter,
          })
        : [],
    // setPartyFilter is stable via patchParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companySlug, listPath, partyFilter],
  );

  const payments =
    paymentsQuery.data?.pages.flatMap((p) => p.payments) ?? [];
  /** Party stays client-side (no partyId list param yet); lifecycle is server. */
  const visible = partyFilter
    ? payments.filter((p) => p.supplierId === partyFilter.id)
    : payments;
  const total = paymentsQuery.data?.pages[0]?.total;
  const loading = paymentsQuery.isPending;
  const error = errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;
  const filtersActive =
    Boolean(query.trim()) ||
    Boolean(partyFilter) ||
    lifecycleFilter !== "all";
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery: setQuery,
    canResetToDefault: filtersActive,
    canClearAll: filtersActive,
  });
  const searchingOnline = listSearchOnlinePending({
    query,
    debouncedQ,
    isFetching: paymentsQuery.isFetching,
  });

  useEffect(() => {
    if (!companySlug || loading || listReturnHandled.current) return;
    listReturnHandled.current = true;
    const id = takeListReturn(listPath);
    if (id) flashRow(id);
  }, [companySlug, loading, listPath, flashRow]);

  if (!companySlug) return null;

  return (
    <PageFrame>
      <DataTableChrome>
        <DeskListFilterReset {...filterReset} />
        <DeskListHeader
          title="Supplier payments"
          description="Pays and refunds — search by number, supplier, or bank ref"
          tabs={
            <DeskSubNav
              placement="header"
              items={purchasingModuleTabs(companySlug)}
              aria-label="Purchasing views"
            />
          }
        />

        <AdminFilters>
          <input
            className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
            placeholder="Search number, supplier, bank ref…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={loading && payments.length === 0}
          />
          <select
            className={adminFilterControlClass}
            value={lifecycleFilter}
            onChange={(e) =>
              setLifecycleFilter(e.target.value as PostedDocLifecycleFilter)
            }
          >
            <option value="all">All lifecycle</option>
            <option value="draft">Draft</option>
            <option value="posted">Posted</option>
            <option value="void">Void</option>
          </select>
          <SavedListViewsControl
            listId="supplier-payments"
            companySlug={companySlug}
            currentQs={searchParams.toString()}
            onApplyQs={(qs) => {
              replaceAllParams(qs);
              const q = readQ(new URLSearchParams(qs));
              setQuery(q);
              setDebouncedQ(q);
            }}
          />
          <ActiveFilterChips
            chips={[
              ...(partyFilter
                ? [
                    {
                      key: "party",
                      label: `Supplier: ${partyFilter.name}`,
                      onClear: () => setPartyFilter(null),
                    },
                  ]
                : []),
              ...(lifecycleFilter !== "all"
                ? [
                    {
                      key: "lifecycle",
                      label:
                        lifecycleFilter[0]!.toUpperCase() +
                        lifecycleFilter.slice(1),
                      onClear: () => setLifecycleFilter("all"),
                    },
                  ]
                : []),
            ]}
          />
        </AdminFilters>
        <ListSearchStatus online searching={searchingOnline} />
        {visible.length > 0 && total != null ? (
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            Showing {visible.length}
            {total > payments.length
              ? ` · loaded ${payments.length} of ${total}`
              : ""}
          </p>
        ) : null}

        {error && (
          <Alert variant="destructive" className="mt-4">
            {error}
          </Alert>
        )}
        {loading && payments.length === 0 && (
          <Alert variant="muted" className="mt-8">
            Loading payments…
          </Alert>
        )}

        {!loading && visible.length === 0 && !error && (
          <div className="mt-8">
            <EmptyState
              title={
                filtersActive
                  ? "No payments match"
                  : "No supplier payments yet"
              }
              description={
                filtersActive
                  ? "Try clearing search or lifecycle filter."
                  : "Pay from an open purchase order when the bill is due."
              }
              action={
                filtersActive ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => filterReset.onClearAll()}
                  >
                    Clear filters
                  </Button>
                ) : (
                  <Button asChild size="sm" variant="outline">
                    <Link
                      to={`/c/${companySlug}/purchasing/to-pay`}
                      className="no-underline"
                    >
                      To pay
                    </Link>
                  </Button>
                )
              }
            />
          </div>
        )}

        {visible.length > 0 ? (
          <>
            <DocListLegend kinds={POSTED_DOC_LIFECYCLE_LEGEND} />
            <DataTable
              onRefresh={() => void paymentsQuery.refetch()}
              listId="supplier-payments"
              companySlug={companySlug}
              columns={columns}
              data={visible}
              getRowId={(r) => r.id}
              sorting={sorting}
              onSortingChange={setSorting}
              highlightedRowId={flashId}
              defaultVisibility={{
                bankRef: false,
                payMethodName: false,
                updatedAt: true,
              }}
            />
            {paymentsQuery.hasNextPage ? (
              <div className="mt-4 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  disabled={paymentsQuery.isFetchingNextPage}
                  pending={paymentsQuery.isFetchingNextPage}
                  pendingLabel="Loading more…"
                  onClick={() => void paymentsQuery.fetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        ) : null}

        <p className="mt-8 text-sm">
          <Link to={`/c/${companySlug}/purchasing/orders`} className="no-underline">
            ← Purchase orders
          </Link>
        </p>
      </DataTableChrome>
    </PageFrame>
  );
}
